import type { FetchPageServiceParameters } from "~/core/composables/useFetchPageV2";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import RestService from "~/core/services/rest.service";
import type { CreateInterfaceRequestPayload } from "~/modules/applicationManagement/models/CreateInterfaceRequestPayload.interface";
import type { CreateInterfaceResponse } from "~/modules/applicationManagement/models/CreateInterfaceResponse.interface";
import type { Interface } from "~/modules/applicationManagement/models/Interface.interface";
import type { UpdateInterfaceRequestPayload } from "~/modules/applicationManagement/models/UpdateInterfaceRequestPayload.interface";
import organizationService from "~/modules/organization/services/organization.service";

export class ApplicationMgtService extends RestService {
  /**
   * get App Interfaces
   * @param page
   * @param pageSize
   * @param sort
   * @param filter
   * @returns
   */
  getAppInterfaces({
    organizationCode,
    page,
    pageSize,
    searchParams,
    sort,
  }: FetchPageServiceParameters): Promise<PaginatedList<Interface>> {
    const params = { page, pageSize, sort, filter: searchParams };
    return this.$apiV2.get(
      this.getOrganizationAppInterfacesPath(organizationCode),
      {
        params,
      },
    );
  }

  /**
   * Create an application interface
   * @param appInterface the new interface payload
   * @returns
   */
  createAppInterface(
    appInterface: CreateInterfaceRequestPayload,
  ): Promise<CreateInterfaceResponse> {
    return this.$apiV2.post(
      this.getOrganizationAppInterfacesPath(appInterface.organizationCode),
      appInterface,
    );
  }

  /**
   * Get an interface
   * @param clientId
   * @param organizationCode
   * @returns
   */
  getAppInterfaceByClientId(
    clientId: string,
    organizationCode?: number,
  ): Promise<Interface> {
    return this.$apiV2.get(
      this.getOrganizationAppInterfacePath(clientId, organizationCode),
    );
  }

  /**
   * Update an interface and return it updated
   * @param clientId
   * @param updateInterfacePayload
   * @param organizationCode
   * @returns
   */
  updateAppInterface(
    clientId: string,
    updateInterfacePayload: UpdateInterfaceRequestPayload,
    organizationCode?: number,
  ) {
    return this.$apiV2.put(
      this.getOrganizationAppInterfacePath(clientId, organizationCode),
      updateInterfacePayload,
    );
  }

  /**
   * Delete an interface
   * @param clientId
   * @param organizationCode
   * @returns
   */
  deleteAppInterface(clientId: string, organizationCode?: number) {
    return this.$apiV2.delete(
      this.getOrganizationAppInterfacePath(clientId, organizationCode),
    );
  }

  /**
   * Deactivate an interface
   * @param clientId
   * @param organizationCode
   * @returns
   */
  deactivateAppInterface(clientId: string, organizationCode?: number) {
    return this.$apiV2.post(
      `${this.getOrganizationAppInterfacePath(clientId, organizationCode)}/deactivate`,
    );
  }

  /**
   * Activate an interface
   * @param clientId
   * @param organizationCode
   * @returns
   */
  activateAppInterface(clientId: string, organizationCode?: number) {
    return this.$apiV2.post(
      `${this.getOrganizationAppInterfacePath(clientId, organizationCode)}/activate`,
    );
  }

  private getOrganizationAppInterfacesPath(organizationCode?: number) {
    return `${organizationService.getOrganizationPath(organizationCode)}/interfaces`;
  }

  private getOrganizationAppInterfacePath(
    clientId: string,
    organizationCode?: number,
  ) {
    return `${this.getOrganizationAppInterfacesPath(organizationCode)}/${clientId}`;
  }
}

export default new ApplicationMgtService();
